import { useEffect, useState } from "react";
import { EXPOSURE_YEAR } from "../../config/exposure";
import { CellLayerTooLargeError, getBaselineCells } from "../../services/exposureApi";
import type { BaselineCells, BaselineMeasure } from "../../services/exposureApi";

export interface BaselineLayer {
  on: boolean;
  setOn: (on: boolean) => void;
  measure: BaselineMeasure;
  setMeasure: (m: BaselineMeasure) => void;
  data: BaselineCells | null;
  loading: boolean;
  /** Why there are no cells to draw: the area is too large, or the request failed. */
  problem: string | null;
}

/**
 * State of the optional "Population baseline" map layer: off until switched on, and it only fetches
 * the 250 m cells while on. Emergency dashboards use it; Real Time never mounts it.
 */
export function useBaselineLayer(geometries: string[]): BaselineLayer {
  const [on, setOn] = useState(false);
  const [measure, setMeasure] = useState<BaselineMeasure>("residents");
  const [data, setData] = useState<BaselineCells | null>(null);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const key = JSON.stringify(geometries);

  useEffect(() => {
    const list: string[] = JSON.parse(key);
    setData(null);
    setProblem(null);
    if (!on || list.length === 0) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getBaselineCells({ geometries: list, year: EXPOSURE_YEAR, measure })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (cancelled) return;
        setProblem(err instanceof CellLayerTooLargeError ? err.message : err instanceof Error ? err.message : "Could not load the cell layer.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key, on, measure]);

  return { on, setOn, measure, setMeasure, data, loading, problem };
}
